// SNIGJ - Enhanced Prisoners Page with Grid View
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { t } from '../lib/i18n';
import { prisonersAPI, referenceAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import BiometricCaptureModal from '../components/BiometricCaptureModal';
import PersonSearchCombo from '../components/shared/PersonSearchCombo';
import OffenseCombobox from '../components/OffenseCombobox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Plus, Search, Filter, Eye, Edit, User, Calendar, MapPin, Camera, Fingerprint, ScanLine,
  LayoutGrid, List, Users, AlertTriangle, Baby, UserCheck, UserX, Loader2, Scale
} from 'lucide-react';
import { toast } from 'sonner';
import { DRC_PROVINCES } from '../lib/drc-data';

// Crime/Offense Types
const CRIME_TYPES = [
  { value: 'VOL', label: { fr: 'Vol', en: 'Theft' } },
  { value: 'VIOL', label: { fr: 'Viol', en: 'Rape' } },
  { value: 'MEURTRE', label: { fr: 'Meurtre/Assassinat', en: 'Murder' } },
  { value: 'DETOURNEMENT', label: { fr: 'Détournement de fonds', en: 'Embezzlement' } },
  { value: 'ESCROQUERIE', label: { fr: 'Escroquerie', en: 'Fraud' } },
  { value: 'TRAFIC_DROGUE', label: { fr: 'Trafic de drogue', en: 'Drug Trafficking' } },
  { value: 'TRAFIC_HUMAINS', label: { fr: 'Trafic d\'êtres humains', en: 'Human Trafficking' } },
  { value: 'VIOLENCE', label: { fr: 'Violence/Coups et blessures', en: 'Assault' } },
  { value: 'CORRUPTION', label: { fr: 'Corruption', en: 'Corruption' } },
  { value: 'TERRORISME', label: { fr: 'Terrorisme', en: 'Terrorism' } },
  { value: 'REBELLION', label: { fr: 'Rébellion', en: 'Rebellion' } },
  { value: 'AUTRE', label: { fr: 'Autre', en: 'Other' } }
];

// Age Categories
const AGE_CATEGORIES = [
  { value: 'MINOR', label: { fr: 'Mineur (<18 ans)', en: 'Minor (<18)' }, icon: Baby },
  { value: 'ADULT', label: { fr: 'Adulte (18-60 ans)', en: 'Adult (18-60)' }, icon: UserCheck },
  { value: 'ELDERLY', label: { fr: 'Personne âgée (>60 ans)', en: 'Elderly (>60)' }, icon: Users }
];

const PrisonersPage = () => {
  const { language, user } = useAuth();
  const navigate = useNavigate();
  const [prisoners, setPrisoners] = useState([]);
  const [prisons, setPrisons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    prison_id: '',
    gender: '',
    age_category: '',
    crime_type: '',
    province: ''
  });
  const [formData, setFormData] = useState({
    user_id: '', // For linking to existing person
    unji: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    address: '',
    date_of_birth: '',
    gender: 'M',
    nationality: 'Congolaise',
    status: 'PREVENTIVE',
    classification: 'RESTRICTED',
    prison_id: '',
    cell_number: '',
    admission_date: new Date().toISOString().split('T')[0],
    sentence_start: '',
    sentence_end: '',
    charges: [],
    crime_types: [],
    biometric_photo: '',
    fingerprints: null,
    retinal_scan: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Biometric modals
  const [showFingerprintModal, setShowFingerprintModal] = useState(false);
  const [showRetinalModal, setShowRetinalModal] = useState(false);

  // Handle person selection from PersonSearchCombo
  const handlePersonSelect = (person) => {
    if (person) {
      setFormData(prev => ({
        ...prev,
        user_id: person.user_id || person.id,
        unji: person.national_id || '',
        first_name: person.first_name || person.name?.split(' ')[0] || '',
        last_name: person.last_name || person.name?.split(' ').slice(1).join(' ') || '',
        phone: person.phone || '',
        email: person.email || '',
        address: person.address || '',
        date_of_birth: person.date_of_birth || '',
        gender: person.gender || 'M'
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        user_id: '',
        unji: '',
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        address: ''
      }));
    }
  };

  useEffect(() => {
    loadPrisoners();
    loadPrisons();
  }, [filters, search]);

  const loadPrisoners = async () => {
    try {
      setLoading(true);
      const params = { ...filters, search, limit: 100 };
      Object.keys(params).forEach(key => !params[key] && delete params[key]);
      const response = await prisonersAPI.list(params);
      setPrisoners(response.data);
    } catch (error) {
      console.error('Failed to load prisoners:', error);
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Failed to load prisoners');
    } finally {
      setLoading(false);
    }
  };

  const loadPrisons = async () => {
    try {
      const response = await referenceAPI.getPrisons();
      setPrisons(response.data);
    } catch (error) {
      console.error('Failed to load prisons:', error);
    }
  };

  const handleCreatePrisoner = async (e) => {
    e.preventDefault();
    try {
      await prisonersAPI.create(formData);
      toast.success(language === 'fr' ? 'Détenu enregistré' : 'Prisoner registered');
      setShowCreateModal(false);
      loadPrisoners();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? "Erreur d'enregistrement" : 'Failed to register prisoner'));
    }
  };

  const resetForm = () => {
    setFormData({
      unji: '', first_name: '', last_name: '', date_of_birth: '', gender: 'M',
      nationality: 'Congolaise', status: 'PREVENTIVE', classification: 'RESTRICTED',
      prison_id: '', cell_number: '', admission_date: new Date().toISOString().split('T')[0],
      sentence_start: '', sentence_end: '', charges: [], crime_types: [], biometric_photo: ''
    });
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, biometric_photo: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      PREVENTIVE: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      SENTENCED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
      RELEASED: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      TRANSFERRED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      DECEASED: 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-400'
    };
    return styles[status] || 'bg-gray-100';
  };

  const getAgeCategory = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    const age = Math.floor((new Date() - new Date(dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000));
    if (age < 18) return 'MINOR';
    if (age > 60) return 'ELDERLY';
    return 'ADULT';
  };

  const clearFilters = () => {
    setFilters({ status: '', prison_id: '', gender: '', age_category: '', crime_type: '', province: '' });
    setSearch('');
  };

  // Statuts disponibles lors de la création (SENTENCED doit venir d'un procès)
  const statuses = ['PREVENTIVE', 'SENTENCED', 'RELEASED', 'TRANSFERRED'];
  
  // Statuts disponibles pour création par rôle
  const getAvailableStatusesForCreation = () => {
    // Seul le MAP (PREVENTIVE) peut être créé directement
    // SENTENCED vient d'un procès, RELEASED/TRANSFERRED viennent de décisions judiciaires
    if (['SUPER_ADMIN', 'NATIONAL_ADMIN', 'TECH_SUPPORT'].includes(user?.role)) {
      return ['PREVENTIVE', 'MAP']; // Admins peuvent créer en préventive
    }
    return ['PREVENTIVE']; // Tous les autres ne peuvent créer qu'en préventive
  };
  
  // Obtenir les cellules de la prison sélectionnée
  const getSelectedPrisonCells = () => {
    if (!formData.prison_id) return [];
    const selectedPrison = prisons.find(p => p.prison_id === formData.prison_id);
    return selectedPrison?.cells || [];
  };

  // Filter prisoners based on criteria
  const filteredPrisoners = prisoners.filter(prisoner => {
    if (filters.age_category) {
      const ageCategory = getAgeCategory(prisoner.date_of_birth);
      if (ageCategory !== filters.age_category) return false;
    }
    if (filters.crime_type && prisoner.crime_types) {
      if (!prisoner.crime_types.includes(filters.crime_type)) return false;
    }
    return true;
  });

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6" data-testid="prisoners-page">
        {/* Header - Mobile Optimized */}
        <div className="flex flex-col gap-3 sm:gap-4">
          <div>
            <h1 className="font-heading text-xl sm:text-2xl lg:text-3xl font-bold tracking-tight">
              {t(language, 'prisoners.title')}
            </h1>
            <p className="text-muted-foreground text-sm sm:text-base mt-1">
              {language === 'fr' ? 'Registre national des détenus' : 'National prisoner registry'}
            </p>
          </div>
          <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-3">
            {/* View Toggle */}
            <div className="flex items-center border rounded-md">
              <Button 
                variant={viewMode === 'grid' ? 'secondary' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('grid')}
                data-testid="view-grid-btn"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
                size="sm"
                onClick={() => setViewMode('list')}
                data-testid="view-list-btn"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
              <DialogTrigger asChild>
                <Button className="gap-2" size="sm" data-testid="new-prisoner-btn">
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">{t(language, 'prisoners.newPrisoner')}</span>
                  <span className="sm:hidden">{language === 'fr' ? 'Nouveau' : 'New'}</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-heading">{t(language, 'prisoners.newPrisoner')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreatePrisoner} className="space-y-4 mt-4">
                  <div className="grid grid-cols-4 gap-4">
                    {/* Biometric Photo */}
                    <div className="row-span-3">
                      <Label>{t(language, 'prisoners.biometricPhoto')}</Label>
                      <div className="mt-2">
                        {formData.biometric_photo ? (
                          <div className="relative w-32 h-40 rounded border overflow-hidden">
                            <img src={formData.biometric_photo} alt="Photo" className="w-full h-full object-cover"/>
                            <Button type="button" variant="ghost" size="sm" className="absolute bottom-1 right-1 h-6 w-6 p-0"
                              onClick={() => setFormData({ ...formData, biometric_photo: '' })}>×</Button>
                          </div>
                        ) : (
                          <label className="biometric-placeholder cursor-pointer">
                            <input type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" data-testid="photo-upload"/>
                            <div className="text-center">
                              <Camera className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
                              <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Ajouter photo' : 'Add photo'}</span>
                            </div>
                          </label>
                        )}
                      </div>
                      <div className="mt-4 space-y-2">
                        {/* Fingerprint Button */}
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-start gap-2"
                          onClick={() => setShowFingerprintModal(true)}
                          data-testid="fingerprint-capture-btn"
                        >
                          <Fingerprint className={`w-5 h-5 ${formData.fingerprints ? 'text-green-500' : 'text-muted-foreground'}`} />
                          <span className="text-xs">
                            {formData.fingerprints 
                              ? (language === 'fr' ? 'Empreintes capturées ✓' : 'Fingerprints captured ✓')
                              : (language === 'fr' ? 'Capturer empreintes' : 'Capture fingerprints')}
                          </span>
                        </Button>
                        {/* Retinal Scan Button */}
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-start gap-2"
                          onClick={() => setShowRetinalModal(true)}
                          data-testid="retinal-capture-btn"
                        >
                          <ScanLine className={`w-5 h-5 ${formData.retinal_scan ? 'text-green-500' : 'text-muted-foreground'}`} />
                          <span className="text-xs">
                            {formData.retinal_scan 
                              ? (language === 'fr' ? 'Scan rétinien capturé ✓' : 'Retinal scan captured ✓')
                              : (language === 'fr' ? 'Capturer scan rétinien' : 'Capture retinal scan')}
                          </span>
                        </Button>
                      </div>
                    </div>

                    {/* Personal Info */}
                    <div className="col-span-3 grid grid-cols-3 gap-4">
                      {/* Person Search Combo - spans full width */}
                      <div className="col-span-3 space-y-2">
                        <PersonSearchCombo
                          label={language === 'fr' ? 'Rechercher la personne (UNJI ou Nom)' : 'Search person (UNJI or Name)'}
                          placeholder={language === 'fr' ? 'Tapez le UNJI ou le nom pour rechercher...' : 'Type UNJI or name to search...'}
                          onPersonSelect={handlePersonSelect}
                          showCreateOption={true}
                        />
                      </div>
                      
                      {/* UNJI - Auto-filled or manual */}
                      <div className="space-y-2">
                        <Label>{t(language, 'prisoners.unji')}</Label>
                        <Input 
                          value={formData.unji} 
                          onChange={(e) => setFormData({ ...formData, unji: e.target.value })}
                          placeholder="UNJI-2024-XXXXX" 
                          className="font-mono" 
                          data-testid="prisoner-unji"
                          disabled={!!formData.user_id}
                        />
                        {formData.user_id && (
                          <p className="text-xs text-green-600">{language === 'fr' ? 'Lié à un compte existant' : 'Linked to existing account'}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        <Label>{t(language, 'prisoners.firstName')}</Label>
                        <Input value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                          required data-testid="prisoner-firstname"/>
                      </div>
                      <div className="space-y-2">
                        <Label>{t(language, 'prisoners.lastName')}</Label>
                        <Input value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                          required data-testid="prisoner-lastname"/>
                      </div>
                      <div className="space-y-2">
                        <Label>{t(language, 'prisoners.dateOfBirth')}</Label>
                        <Input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                          data-testid="prisoner-dob"/>
                      </div>
                      <div className="space-y-2">
                        <Label>{t(language, 'prisoners.gender')}</Label>
                        <Select value={formData.gender} onValueChange={(value) => setFormData({ ...formData, gender: value })}>
                          <SelectTrigger data-testid="prisoner-gender"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M">{t(language, 'prisoners.male')}</SelectItem>
                            <SelectItem value="F">{t(language, 'prisoners.female')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>{t(language, 'prisoners.status')}</Label>
                        <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                          <SelectTrigger data-testid="prisoner-status"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {getAvailableStatusesForCreation().map((s) => (<SelectItem key={s} value={s}>{s === 'MAP' ? 'Mandat d\'Arrêt Provisoire' : t(language, `prisoners.${s.toLowerCase()}`)}</SelectItem>))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground">
                          {language === 'fr' 
                            ? 'Note: Le statut "Condamné" provient d\'un jugement' 
                            : 'Note: "Sentenced" status comes from a court judgment'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Crime Types */}
                  <div className="space-y-2">
                    <Label>{language === 'fr' ? 'Types de délits/crimes' : 'Crime Types'}</Label>
                    <div className="flex flex-wrap gap-2">
                      {CRIME_TYPES.map((crime) => (
                        <Badge key={crime.value}
                          variant={formData.crime_types?.includes(crime.value) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => {
                            const current = formData.crime_types || [];
                            const updated = current.includes(crime.value) 
                              ? current.filter(c => c !== crime.value)
                              : [...current, crime.value];
                            setFormData({ ...formData, crime_types: updated });
                          }}>
                          {crime.label[language]}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  {/* Offense from Legal Library */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-1.5">
                      <Scale className="w-3.5 h-3.5 text-primary" />
                      {language === 'fr' ? 'Qualification juridique (Bibliotheque)' : 'Legal qualification (Library)'}
                    </Label>
                    <OffenseCombobox
                      value={formData.legal_offense || null}
                      onChange={(offense) => setFormData({ ...formData, legal_offense: offense })}
                      placeholder={language === 'fr' ? "Rechercher dans la bibliotheque juridique..." : "Search legal library..."}
                    />
                    {formData.legal_offense && (
                      <div className="text-xs p-2 bg-muted/50 rounded border">
                        <p className="font-medium">{formData.legal_offense.reference}</p>
                        <p className="text-red-600 dark:text-red-400 mt-0.5">{formData.legal_offense.penalty}</p>
                      </div>
                    )}
                  </div>

                  {/* Prison Info */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>{t(language, 'prisoners.prison')}</Label>
                      <Select value={formData.prison_id} onValueChange={(value) => setFormData({ ...formData, prison_id: value, cell_number: '' })}>
                        <SelectTrigger data-testid="prisoner-prison"><SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} /></SelectTrigger>
                        <SelectContent>
                          {prisons.map((prison) => (<SelectItem key={prison.prison_id} value={prison.prison_id}>{prison.name}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>{t(language, 'prisoners.cell')}</Label>
                      {getSelectedPrisonCells().length > 0 ? (
                        <Select value={formData.cell_number} onValueChange={(value) => setFormData({ ...formData, cell_number: value })}>
                          <SelectTrigger data-testid="prisoner-cell"><SelectValue placeholder={language === 'fr' ? 'Sélectionner cellule...' : 'Select cell...'} /></SelectTrigger>
                          <SelectContent>
                            {getSelectedPrisonCells().filter(c => c.is_active !== false).map((cell) => (
                              <SelectItem key={cell.cell_id} value={cell.name}>
                                {cell.name} {cell.pavilion && `(${cell.pavilion})`} - Cap: {cell.capacity}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input value={formData.cell_number} onChange={(e) => setFormData({ ...formData, cell_number: e.target.value })}
                          placeholder="A-101" data-testid="prisoner-cell"/>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label>{t(language, 'prisoners.admissionDate')}</Label>
                      <Input type="date" value={formData.admission_date} onChange={(e) => setFormData({ ...formData, admission_date: e.target.value })}
                        required data-testid="prisoner-admission"/>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>{t(language, 'common.cancel')}</Button>
                    <Button type="submit" data-testid="create-prisoner-submit">{t(language, 'common.save')}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Filters - Mobile Optimized */}
        <Card>
          <CardContent className="p-3 sm:p-4">
            <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3">
              <div className="col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input placeholder={t(language, 'common.search') + '...'} value={search} onChange={(e) => setSearch(e.target.value)}
                    className="pl-10" data-testid="prisoners-search"/>
                </div>
              </div>
              <Select value={filters.gender || "ALL"} onValueChange={(value) => setFilters({ ...filters, gender: value === "ALL" ? "" : value })}>
                <SelectTrigger data-testid="filter-gender" className="text-sm"><SelectValue placeholder={language === 'fr' ? 'Sexe' : 'Gender'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  <SelectItem value="M">{language === 'fr' ? 'Homme' : 'Male'}</SelectItem>
                  <SelectItem value="F">{language === 'fr' ? 'Femme' : 'Female'}</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filters.age_category || "ALL"} onValueChange={(value) => setFilters({ ...filters, age_category: value === "ALL" ? "" : value })}>
                <SelectTrigger data-testid="filter-age"><SelectValue placeholder={language === 'fr' ? 'Âge' : 'Age'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  {AGE_CATEGORIES.map((cat) => (<SelectItem key={cat.value} value={cat.value}>{cat.label[language]}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filters.crime_type || "ALL"} onValueChange={(value) => setFilters({ ...filters, crime_type: value === "ALL" ? "" : value })}>
                <SelectTrigger data-testid="filter-crime"><SelectValue placeholder={language === 'fr' ? 'Délit/Crime' : 'Crime Type'} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                  {CRIME_TYPES.map((crime) => (<SelectItem key={crime.value} value={crime.value}>{crime.label[language]}</SelectItem>))}
                </SelectContent>
              </Select>
              <Select value={filters.province || "ALL"} onValueChange={(value) => setFilters({ ...filters, province: value === "ALL" ? "" : value })}>
                <SelectTrigger data-testid="filter-province"><SelectValue placeholder="Province" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{language === 'fr' ? 'Toutes' : 'All'}</SelectItem>
                  {DRC_PROVINCES.map((prov) => (<SelectItem key={prov.code} value={prov.code}>{prov.name}</SelectItem>))}
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={clearFilters} className="gap-2">
                <Filter className="w-4 h-4" />
                {language === 'fr' ? 'Réinitialiser' : 'Reset'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Grid View - Mobile Optimized */}
        {viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 sm:gap-4">
            {loading ? (
              Array.from({ length: 10 }).map((_, i) => (
                <Card key={i} className="overflow-hidden">
                  <div className="aspect-[3/4] skeleton" />
                  <CardContent className="p-3 space-y-2">
                    <div className="h-4 skeleton" />
                    <div className="h-3 skeleton w-2/3" />
                  </CardContent>
                </Card>
              ))
            ) : filteredPrisoners.length === 0 ? (
              <div className="col-span-full text-center py-12">
                <Users className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">{t(language, 'common.noData')}</p>
              </div>
            ) : (
              filteredPrisoners.map((prisoner) => (
                <Card key={prisoner.prisoner_id} className="overflow-hidden hover-lift cursor-pointer group"
                  onClick={() => navigate(`/prisoners/${prisoner.prisoner_id}`)}
                  data-testid={`prisoner-card-${prisoner.prisoner_id}`}>
                  <div className="aspect-[3/4] bg-muted relative">
                    {prisoner.biometric_photo ? (
                      <img src={prisoner.biometric_photo} alt={`${prisoner.first_name} ${prisoner.last_name}`}
                        className="w-full h-full object-cover"/>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-16 h-16 text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      <Badge className={getStatusBadge(prisoner.status)}>{t(language, `prisoners.${prisoner.status.toLowerCase()}`)}</Badge>
                    </div>
                    {prisoner.crime_types?.includes('MEURTRE') && (
                      <div className="absolute top-2 left-2">
                        <Badge variant="destructive" className="gap-1"><AlertTriangle className="w-3 h-3" /></Badge>
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); navigate(`/prisoners/${prisoner.prisoner_id}`); }}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="secondary" onClick={(e) => { e.stopPropagation(); navigate(`/prisoners/${prisoner.prisoner_id}/edit`); }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <p className="font-medium truncate">{prisoner.first_name} {prisoner.last_name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{prisoner.unji}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                      <span>{prisoner.gender === 'M' ? '♂' : '♀'}</span>
                      <span>•</span>
                      <span>{prisons.find(p => p.prison_id === prisoner.prison_id)?.name?.split(' ')[0] || '—'}</span>
                    </div>
                    {prisoner.crime_types?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {prisoner.crime_types.slice(0, 2).map((ct) => (
                          <Badge key={ct} variant="outline" className="text-xs py-0">
                            {CRIME_TYPES.find(c => c.value === ct)?.label[language] || ct}
                          </Badge>
                        ))}
                        {prisoner.crime_types.length > 2 && (
                          <Badge variant="outline" className="text-xs py-0">+{prisoner.crime_types.length - 2}</Badge>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        ) : (
          /* List View */
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr className="bg-muted/50">
                      <th>Photo</th>
                      <th>{t(language, 'prisoners.unji')}</th>
                      <th>{language === 'fr' ? 'Nom' : 'Name'}</th>
                      <th>{t(language, 'prisoners.gender')}</th>
                      <th>{language === 'fr' ? 'Âge' : 'Age'}</th>
                      <th>{t(language, 'prisoners.status')}</th>
                      <th>{language === 'fr' ? 'Délits/Crimes' : 'Crimes'}</th>
                      <th>{t(language, 'prisoners.prison')}</th>
                      <th>{t(language, 'cases.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>{Array.from({ length: 9 }).map((_, j) => (<td key={j}><div className="h-4 skeleton" /></td>))}</tr>
                      ))
                    ) : filteredPrisoners.length === 0 ? (
                      <tr><td colSpan={9} className="text-center py-8 text-muted-foreground">{t(language, 'common.noData')}</td></tr>
                    ) : (
                      filteredPrisoners.map((prisoner) => (
                        <tr key={prisoner.prisoner_id} data-testid={`prisoner-row-${prisoner.prisoner_id}`}>
                          <td>
                            {prisoner.biometric_photo ? (
                              <img src={prisoner.biometric_photo} alt="" className="w-10 h-12 rounded object-cover"/>
                            ) : (
                              <div className="w-10 h-12 rounded bg-muted flex items-center justify-center">
                                <User className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                          </td>
                          <td><span className="font-mono text-sm">{prisoner.unji}</span></td>
                          <td>{prisoner.first_name} {prisoner.last_name}</td>
                          <td>{prisoner.gender === 'M' ? '♂ ' + t(language, 'prisoners.male') : '♀ ' + t(language, 'prisoners.female')}</td>
                          <td>
                            {prisoner.date_of_birth && (
                              <span>{Math.floor((new Date() - new Date(prisoner.date_of_birth)) / (365.25 * 24 * 60 * 60 * 1000))} ans</span>
                            )}
                          </td>
                          <td><Badge className={getStatusBadge(prisoner.status)}>{t(language, `prisoners.${prisoner.status.toLowerCase()}`)}</Badge></td>
                          <td>
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {prisoner.crime_types?.slice(0, 2).map((ct) => (
                                <Badge key={ct} variant="outline" className="text-xs">{CRIME_TYPES.find(c => c.value === ct)?.label[language] || ct}</Badge>
                              ))}
                            </div>
                          </td>
                          <td>{prisons.find(p => p.prison_id === prisoner.prison_id)?.name || '—'}</td>
                          <td>
                            <div className="flex items-center gap-2">
                              <Button variant="ghost" size="icon" onClick={() => navigate(`/prisoners/${prisoner.prisoner_id}`)}><Eye className="w-4 h-4" /></Button>
                              <Button variant="ghost" size="icon" onClick={() => navigate(`/prisoners/${prisoner.prisoner_id}/edit`)}><Edit className="w-4 h-4" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Biometric Capture Modals */}
      <BiometricCaptureModal
        captureType="fingerprint"
        open={showFingerprintModal}
        onClose={() => setShowFingerprintModal(false)}
        onCapture={(data) => {
          setFormData({ ...formData, fingerprints: data });
          setShowFingerprintModal(false);
          toast.success(language === 'fr' ? 'Empreintes capturées avec succès' : 'Fingerprints captured successfully');
        }}
      />
      <BiometricCaptureModal
        captureType="retinal"
        open={showRetinalModal}
        onClose={() => setShowRetinalModal(false)}
        onCapture={(data) => {
          setFormData({ ...formData, retinal_scan: data });
          setShowRetinalModal(false);
          toast.success(language === 'fr' ? 'Scan rétinien capturé avec succès' : 'Retinal scan captured successfully');
        }}
      />
    </DashboardLayout>
  );
};

export default PrisonersPage;
